import { Event, ThemeType, Visibility } from "@markit/common.types";
import { CSSProperties, ReactNode, useCallback, useMemo } from "react";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { Icon } from "@iconify/react";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { Colors } from "../../../../utils/colors";
import ThemeDropdownToggle from "./ThemeDropdownToggle";

type ThemeVisibilityOptionsProps = {
  productSettings: Event;
  updateProductSettings: (update: Partial<Event>) => void;
  containerStyles?: CSSProperties;
  displayOption?: "theme" | "visibility" | "both";
  alignment: "row" | "column";
};

const ThemeVisibilityOptions = (props: ThemeVisibilityOptionsProps) => {
  const {
    productSettings,
    updateProductSettings,
    containerStyles,
    displayOption = "both",
    alignment = "column",
  } = props;

  const visibilityItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Public",
        key: "public",
        onPress: () =>
          updateProductSettings({
            visibility: Visibility.Public,
          }),
      },
      {
        title: "Hidden",
        key: "hidden",
        onPress: () =>
          updateProductSettings({
            visibility: Visibility.Private,
          }),
      },
    ],
    [updateProductSettings]
  );

  const renderSettingsDropdownItem = useCallback(
    (title: string, dropDownMenu: ReactNode, subtext?: string) => (
      <StandardBorderedContainer
        containerStyles={{
          flex: alignment === "row" ? 1 : undefined,
          paddingInline: 10,
          borderRadius: 12,
          backgroundColor: Colors.WHITE1,
          height: 62,
          display: "flex",
          alignItems: "center",
          border: `1.5px solid ${Colors.GRAY11}`,
          ...containerStyles,
        }}
      >
        <div className="AlignedRowSpaced" style={{ flex: 1 }}>
          <div className="ColumnNormal" style={{ gap: 4 }}>
            <div className="bodyMedium">{title}</div>
            {subtext ? (
              <span
                className="smallBodySubtext"
                style={{ whiteSpace: "nowrap", fontSize: 11 }}
              >
                {subtext}
              </span>
            ) : null}
          </div>
          {dropDownMenu}
        </div>
      </StandardBorderedContainer>
    ),
    [alignment, containerStyles]
  );

  return (
    <div
      className={alignment === "column" ? "ColumnNormal" : "AlignedRow"}
      style={{ gap: 10, flex: 1 }}
    >
      {displayOption === "theme" || displayOption === "both"
        ? renderSettingsDropdownItem(
            "Theme",
            <ThemeDropdownToggle
              visualThemeType={ThemeType.Light}
              themeType={productSettings.theme}
              onPress={(theme) => updateProductSettings({ theme })}
            />
          )
        : null}
      {displayOption === "visibility" || displayOption === "both"
        ? renderSettingsDropdownItem(
            "Visibility",
            <DropdownMenu
              dropdownItems={visibilityItems}
              trigger={["click"]}
              placement="bottomRight"
              altWidth={150}
            >
              <div className="AlignedRow" style={{ gap: 4 }}>
                {productSettings.visibility === Visibility.Private ? (
                  <span
                    className="bodyMedium"
                    style={{ color: Colors.ORANGE1 }}
                  >
                    Hidden
                  </span>
                ) : (
                  <span className="bodyMedium">Public</span>
                )}
                <Icon
                  icon={"ion:code"}
                  height={18}
                  style={{ transform: "rotate(90deg)" }}
                />
              </div>
            </DropdownMenu>,
            "Feature public items in profile"
          )
        : null}
    </div>
  );
};

export default ThemeVisibilityOptions;
